import { isProduction } from '../utils';
import { config } from '@cotera/client/config/config';
import { Org, Whoami } from '@cotera/api';
import posthog from 'posthog-js';
import { useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  ActionResult,
  ActionPayload,
  Action,
  type ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';

export class AnalyticsClient {
  static init() {
    if (isProduction() && config.posthogKey) {
      posthog.init(config.posthogKey, {
        autocapture: {
          url_allowlist: ['cotera.co/.*'],
        },
        mask_all_text: false,
        mask_all_element_attributes: false,
      });
    }
  }

  identify(org: Org, whoami: Whoami) {
    if (config.posthogKey) {
      posthog.identify(whoami.id, {
        email: whoami.email,
        name: whoami.name,
      });
      posthog.group('organization', org.id, {
        name: org.name,
      });
    }
  }

  identifyByEmail(email: string, firstName: string, lastName: string) {
    if (config.posthogKey) {
      posthog.identify(email, {
        email,
        firstName,
        lastName,
        name: `${firstName} ${lastName}`,
      });
    }
  }

  alias(distinctId: string, alias: string) {
    if (config.posthogKey) {
      posthog.alias(distinctId, alias);
    }
  }

  identifyById(whoami: Whoami) {
    if (config.posthogKey) {
      posthog.identify(whoami.id, {
        email: whoami.email,
        name: whoami.name,
      });
    }
  }

  track(event: string, context?: Record<string, any>) {
    if (config.posthogKey) {
      posthog.capture(event, context);
    }
  }

  trackAction<P extends ActionPayload, Errors extends { t: string }[]>(
    action: Action<P, Errors>,
    payload: Partial<P>,
    result: ActionResult<Errors>,
    metadata?: ActionTrackMetadata
  ) {
    const eventName = (metadata?.event ?? action.title).toLowerCase();
    const baseContext = {
      action: action.constructor.name,
      ...metadata?.properties,
    };
    if (result.isOk()) {
      this.track(eventName, {
        success: true,
        payload,
        ...baseContext,
      });
    } else {
      this.track(eventName, {
        success: false,
        error: result.error,
        payload,
        ...baseContext,
      });
    }
  }
}

export const useAnalytics = (): AnalyticsClient => {
  return useMemo(() => new AnalyticsClient(), []);
};

/**
 * Track an analytics event when the given args change.
 * Effect dependencies are inferred from the args values.
 */
export const useTracking = (
  event: string,
  args: Record<string, unknown>,
  options?: { enabled?: boolean }
): void => {
  const analytics = useAnalytics();
  const enabled = options?.enabled ?? true;
  const values = Object.values(args);
  useEffect(() => {
    if (enabled) {
      analytics.track(event, args);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps inferred from args values
  }, [analytics, event, enabled, ...values]);
};

/**
 * Fire an analytics event when the component unmounts.
 * The callback runs at unmount time and should return the event name and
 * properties to capture. Useful for session-duration style events where
 * the final data is only known at teardown.
 */
export const useTrackingOnExit = (
  getEvent: () => { event: string; args: Record<string, unknown> }
): void => {
  const analytics = useAnalytics();
  const getEventRef = useRef(getEvent);
  getEventRef.current = getEvent;

  useEffect(() => {
    return () => {
      const { event, args } = getEventRef.current();
      analytics.track(event, args);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fire only on unmount
  }, [analytics]);
};

/**
 * Simple text field tracking with 1-minute debounce
 */
export const useTextFieldTracking = (eventName: string) => {
  const analytics = useAnalytics();

  return useDebouncedCallback((value: string) => {
    analytics.track(eventName, {
      length: value.length,
      hasContent: value.length > 0,
    });
  }, 60000);
};
